import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from './Button';
import { ArchiveIcon, UnarchiveIcon } from './ArchiveIcons';
import { useToast } from './ToastProvider';
import { api } from '@/lib/api';

/**
 * The one control that archives a row, or puts it back.
 *
 * It exists because the obvious way — an Archive button on every row — went
 * wrong in the obvious way: with archived rows on screen, the archived ones
 * still offered Archive, and pressing it asked whether to archive something
 * that already was. The client found it immediately.
 *
 * Fixing that list by list would have left the next list to get it wrong, so
 * the decision is made here instead. A row cannot offer the wrong action,
 * because no screen chooses the action any more — it passes the row's state
 * and this picks.
 *
 * Archiving asks first, because it takes a record off everybody's list and
 * the confirmation is where the consequence is explained. Restoring does not:
 * it is the undo, and an undo that needs confirming is not much of one.
 */
export function ArchiveAction({
  archived,
  name,
  path,
  noun,
  invalidate,
  onArchive,
}: {
  archived: boolean;
  /** For the label and the toast: "Chi Mak is back on the list". */
  name: string;
  /** The record's own path, e.g. `/people/{id}`. Restore posts to `{path}/restore`. */
  path: string;
  noun: string;
  /** Query keys to refresh after a restore. */
  invalidate: unknown[];
  /** Archiving goes through the screen's own confirmation. */
  onArchive: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [restoring, setRestoring] = useState(false);

  if (!archived) {
    return (
      <Button
        size="icon"
        variant="ghost"
        className="h-8 w-8"
        aria-label={`Archive ${name}`}
        title={`Archive this ${noun}`}
        leadingIcon={<ArchiveIcon />}
        onClick={onArchive}
      />
    );
  }

  const restore = async () => {
    setRestoring(true);

    try {
      await api.post(`${path}/restore`, {});
      await queryClient.invalidateQueries({ queryKey: invalidate });
      toast.success(`${name} is back on the list`);
    } catch {
      toast.error(`${name} could not be restored.`);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Button
      size="icon"
      variant="ghost"
      className="h-8 w-8"
      aria-label={`Restore ${name}`}
      title={`Restore this ${noun}`}
      loading={restoring}
      leadingIcon={<UnarchiveIcon />}
      onClick={restore}
    />
  );
}
